import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useState } from "react";
import {
  ShieldCheck,
  KeyRound,
  CreditCard,
  Wallet,
  Users,
  Activity,
  LogOut,
  Sun,
  Moon,
  Monitor,
  Vault,
  Layers,
  Banknote,
  ArrowLeftRight,
  ChevronDown,
  Code2,
} from "lucide-react";

const THEMES = [
  { key: "light", icon: Sun },
  { key: "dark", icon: Moon },
  { key: "system", icon: Monitor },
];

export function Sidebar() {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === "admin";
  const [cardsOpen, setCardsOpen] = useState(location.pathname.startsWith("/cards"));

  const cardChildren = [
    ...(isAdmin ? [{ to: "/cards/all", label: "All Cards" }] : []),
    { to: "/cards/mine", label: "My Cards" },
    { to: "/cards/new", label: "New Card" },
  ];

  const topItems = [{ to: "/dashboard", label: "Security Dashboard", icon: ShieldCheck }];
  const midItems = [
    { to: "/credentials", label: "API Credentials", icon: KeyRound },
    { to: "/developer-keys", label: "API Keys", icon: Code2 },
    { to: "/card-vault", label: "Card Vault & Tokens", icon: CreditCard },
    { to: "/crypto-vault", label: "Crypto Key Vault", icon: Wallet },
    { to: "/wallets", label: "Wallets", icon: Wallet },
    { to: "/accounts", label: "Accounts", icon: Banknote },
    { to: "/transactions", label: "Transactions", icon: ArrowLeftRight },
    { to: "/audit", label: "Audit Trail", icon: Activity },
  ];
  if (isAdmin) midItems.push({ to: "/merchants", label: "Merchant Management", icon: Users });

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
      isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
    }`;

  return (
    <aside
      className="fixed left-0 top-0 bottom-0 z-40 flex w-64 flex-col border-r border-border bg-card xl:w-72"
      data-testid="sidebar"
    >
      <div className="flex items-center gap-2.5 border-b border-border px-5 py-5">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
          <Vault className="h-5 w-5" />
        </div>
        <div className="leading-tight">
          <div className="text-base font-bold tracking-tight">CITIDAN</div>
          <div className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-primary">
            <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
            AES-256 Vault Active
          </div>
        </div>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
        {topItems.map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} data-testid={`nav-${to.replace("/", "")}`} className={linkCls}>
            <Icon className="h-4 w-4 shrink-0" /> {label}
          </NavLink>
        ))}

        <button
          onClick={() => setCardsOpen((o) => !o)}
          data-testid="nav-cards-group"
          className={`flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
            location.pathname.startsWith("/cards") ? "text-primary" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
          }`}
        >
          <Layers className="h-4 w-4 shrink-0" /> Cards
          <ChevronDown className={`ml-auto h-4 w-4 transition-transform ${cardsOpen ? "rotate-180" : ""}`} />
        </button>
        {cardsOpen && (
          <div className="ml-4 space-y-1 border-l border-border pl-2">
            {cardChildren.map(({ to, label }) => (
              <NavLink key={to} to={to} data-testid={`nav-${to.replace(/\//g, "-").slice(1)}`}
                className={({ isActive }) =>
                  `block rounded-md px-3 py-2 text-sm transition-colors ${
                    isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                  }`}>
                {label}
              </NavLink>
            ))}
          </div>
        )}

        {midItems.map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} data-testid={`nav-${to.replace("/", "")}`} className={linkCls}>
            <Icon className="h-4 w-4 shrink-0" /> {label}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-border p-3">
        <div className="flex items-center gap-3 rounded-md px-2 py-2" data-testid="sidebar-user">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-sm font-semibold uppercase">
            {user?.email?.[0] || "U"}
          </div>
          <div className="min-w-0 flex-1 leading-tight">
            <div className="truncate text-sm font-medium" data-testid="sidebar-email">
              {user?.email}
            </div>
            <span
              className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider ${
                user?.role === "admin"
                  ? "bg-accent/15 text-accent"
                  : "bg-primary/15 text-primary"
              }`}
            >
              {user?.role}
            </span>
          </div>
          <button
            onClick={() => {
              logout();
              navigate("/login");
            }}
            data-testid="logout-btn"
            className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
            title="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-2 flex items-center gap-1 rounded-lg bg-muted/60 p-1" data-testid="theme-toggle">
          {THEMES.map(({ key, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTheme(key)}
              data-testid={`theme-${key}`}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-md py-1.5 text-xs font-medium capitalize transition-colors ${
                theme === key
                  ? "bg-card text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {key}
            </button>
          ))}
        </div>
      </div>
    </aside>
  );
}
